import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';

type PublicSchema = Database['public'];

export type ConsentTypeKey = PublicSchema['Enums']['consent_type'];
export type ConsentDecision = PublicSchema['Enums']['consent_decision'];
export type ConsentGivenByKind = PublicSchema['Enums']['consent_given_by_kind'];
export type ConsentRecordMethod = PublicSchema['Enums']['consent_record_method'];
export type ConsentIdentityVerification = PublicSchema['Enums']['consent_identity_verification'];
export type CommunicationServiceChannel = PublicSchema['Enums']['communication_service_channel'];

export type ConsentRecordRow = PublicSchema['Tables']['consent_records']['Row'];
export type ConsentTextVersionRow = PublicSchema['Tables']['consent_text_versions']['Row'];
export type CommunicationPreferenceRow = PublicSchema['Tables']['communication_preferences']['Row'];
export type PatientConsentCurrentRow = PublicSchema['Views']['patient_consent_current']['Row'];
export type PatientCommunicationPreferenceCurrentRow =
  PublicSchema['Views']['patient_communication_preference_current']['Row'];

export type ConsentEffectiveStatus = ConsentDecision | 'expired';

export type RecordConsentArgs = PublicSchema['Functions']['record_consent']['Args'];
export type WithdrawConsentArgs = PublicSchema['Functions']['withdraw_consent']['Args'];
export type SetCommunicationPreferenceArgs =
  PublicSchema['Functions']['set_communication_preference']['Args'];
export type CreateConsentTextVersionArgs =
  PublicSchema['Functions']['create_consent_text_version']['Args'];

export const consentRecordsQueryKey = (patientId: string | null) =>
  ['consent-records', patientId] as const;
export const patientConsentCurrentQueryKey = (patientId: string | null) =>
  ['patient-consent-current', patientId] as const;
export const consentTextVersionsQueryKey = (clinicId: string | null, consentType: ConsentTypeKey | null) =>
  ['consent-text-versions', clinicId, consentType] as const;
export const communicationPreferenceQueryKey = (patientId: string | null) =>
  ['communication-preference', patientId] as const;

/** Full append-only consent log for a patient, newest first. */
export function useConsentRecords(
  patientId: string | null | undefined,
  clinicId?: string | null,
) {
  return useQuery({
    queryKey: clinicId
      ? ([...consentRecordsQueryKey(patientId ?? null), clinicId] as const)
      : consentRecordsQueryKey(patientId ?? null),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<ConsentRecordRow[]> => {
      if (!patientId) return [];
      let recordsQuery = supabase.from('consent_records').select('*').eq('patient_id', patientId);
      if (clinicId) recordsQuery = recordsQuery.eq('clinic_id', clinicId);
      const { data, error } = await recordsQuery.order('seq', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 15_000,
  });
}

/** Latest record per consent type + scope (expiry applied in the view). */
export function usePatientConsentCurrent(
  patientId: string | null | undefined,
  clinicId?: string | null,
) {
  return useQuery({
    queryKey: clinicId
      ? ([...patientConsentCurrentQueryKey(patientId ?? null), clinicId] as const)
      : patientConsentCurrentQueryKey(patientId ?? null),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<PatientConsentCurrentRow[]> => {
      if (!patientId) return [];
      let currentQuery = supabase
        .from('patient_consent_current')
        .select('*')
        .eq('patient_id', patientId);
      if (clinicId) currentQuery = currentQuery.eq('clinic_id', clinicId);
      const { data, error } = await currentQuery.order('decided_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 15_000,
  });
}

/** Published text versions visible to the clinic (clinic texts + Clinio-wide), newest first. */
export function useConsentTextVersions(
  clinicId: string | null | undefined,
  consentType?: ConsentTypeKey | null,
) {
  return useQuery({
    queryKey: consentTextVersionsQueryKey(clinicId ?? null, consentType ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ConsentTextVersionRow[]> => {
      if (!clinicId) return [];
      let query = supabase
        .from('consent_text_versions')
        .select('*')
        .or(`clinic_id.eq.${clinicId},clinic_id.is.null`)
        .order('created_at', { ascending: false });
      if (consentType) query = query.eq('consent_type', consentType);
      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });
}

export function usePatientCommunicationPreference(
  patientId: string | null | undefined,
  clinicId?: string | null,
) {
  return useQuery({
    queryKey: clinicId
      ? ([...communicationPreferenceQueryKey(patientId ?? null), clinicId] as const)
      : communicationPreferenceQueryKey(patientId ?? null),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<PatientCommunicationPreferenceCurrentRow | null> => {
      if (!patientId) return null;
      let preferenceQuery = supabase
        .from('patient_communication_preference_current')
        .select('*')
        .eq('patient_id', patientId);
      if (clinicId) preferenceQuery = preferenceQuery.eq('clinic_id', clinicId);
      const { data, error } = await preferenceQuery.maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 15_000,
  });
}

function invalidatePatientConsent(queryClient: ReturnType<typeof useQueryClient>, patientId: string) {
  queryClient.invalidateQueries({ queryKey: consentRecordsQueryKey(patientId) });
  queryClient.invalidateQueries({ queryKey: patientConsentCurrentQueryKey(patientId) });
}

export function useRecordConsent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: RecordConsentArgs): Promise<ConsentRecordRow> => {
      const { data, error } = await supabase.rpc('record_consent', args);
      if (error) throw error;
      return data;
    },
    onSuccess: (row) => invalidatePatientConsent(queryClient, row.patient_id),
  });
}

export function useWithdrawConsent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: WithdrawConsentArgs): Promise<ConsentRecordRow> => {
      const { data, error } = await supabase.rpc('withdraw_consent', args);
      if (error) throw error;
      return data;
    },
    onSuccess: (row) => invalidatePatientConsent(queryClient, row.patient_id),
  });
}

export function useSetCommunicationPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: SetCommunicationPreferenceArgs): Promise<CommunicationPreferenceRow> => {
      const { data, error } = await supabase.rpc('set_communication_preference', args);
      if (error) throw error;
      return data;
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: communicationPreferenceQueryKey(row.patient_id) });
    },
  });
}

export function useCreateConsentTextVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: CreateConsentTextVersionArgs): Promise<ConsentTextVersionRow> => {
      const { data, error } = await supabase.rpc('create_consent_text_version', args);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consent-text-versions'] });
    },
  });
}
